import { convertContinuousProfilingProfile } from "@platformsh/observability-graph/ContinuousProfiling";
import { getUnixTime } from "date-fns";

import { apiProxyRequest } from "Api/apiProxyRequest";
import { toLogFilterParam } from "Api/observability/pipeline/logFilterParams";
import {
  ObservabilityPipelineProjectConfiguration,
  ObservabilityPipelineProjectRetention,
  ObservabilityProjectConfigurationLink
} from "Reducers/observability/pipeline";

import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import { MAX_TOP_HITS } from "src/organization/pages/project/environment/metrics/constant";
import { FilterSelectValueMap } from "src/organization/pages/project/environment/metrics/logs/components/overview/FilterSelect/types";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

import {
  GetContinuousProfilingFlamebearerDataType,
  GetContinuousProfilingTimelineDataType,
  GetResourceByServiceType,
  ObservabilityPipelineHttpMetricsTimeline,
  ObservabilityPipelineContinuousProfilingApps,
  ObservabilityPipelineContinuousProfilingFlamebearerData,
  ObservabilityPipelineContinuousProfilingTimelineData,
  ObservabilityPipelineResourcesByService,
  ObservabilityPipelineResourcesOverview,
  ObservabilityPipelineLogs,
  ObservabilityPipelineLogsOverview
} from "./types";

// Resolves a timeframe to a date range, throwing if resolution fails.
// Used at the API boundary where a valid range is required for the request.
function requireTimeframeRange(timeframe: TimeFrameType) {
  const range = timeframeToRange(timeframe);
  if (!range) {
    throw new Error(
      "Invalid timeframe: could not resolve to a valid date range"
    );
  }
  return range;
}

type ProfilingProfileInput = Parameters<
  typeof convertContinuousProfilingProfile
>[0];

class ObservabilityPipelineHttpClient {
  private projectId: string;
  private branchMachineName: string;
  private configuration: ObservabilityPipelineProjectConfiguration;

  constructor(
    projectId: string,
    branchMachineName: string,
    configuration: ObservabilityPipelineProjectConfiguration
  ) {
    this.projectId = projectId;
    this.branchMachineName = branchMachineName;
    this.configuration = configuration;
  }

  getRetention(): ObservabilityPipelineProjectRetention {
    return this.configuration.retention;
  }

  getMaxRange(): ObservabilityPipelineProjectRetention {
    if (!this.configuration.data_retention) {
      return this.configuration.retention;
    }

    return {
      resources: this.configuration.data_retention.resources.max_range,
      logs: this.configuration.data_retention.logs.max_range,
      http_traffic: this.configuration.data_retention.http_traffic.max_range,
      continuous_profiling:
        this.configuration.data_retention.continuous_profiling.max_range
    };
  }

  getContinuousProfilingApps(
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineContinuousProfilingApps> {
    const action = "conprof_applications";
    this.assertActionSupport(action);

    return this.get<ObservabilityPipelineContinuousProfilingApps>(
      this.configuration._links[action].href,
      signal
    );
  }

  getConfigurationLink(link: ObservabilityProjectConfigurationLink) {
    return this.configuration._links[link];
  }

  getContinuousProfilingTimelineData(
    props: GetContinuousProfilingTimelineDataType,
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineContinuousProfilingTimelineData> {
    const action = "conprof_timeline";
    this.assertActionSupport(action);

    const { start, end } = requireTimeframeRange(props.timeFrame);
    return apiProxyRequest<ObservabilityPipelineContinuousProfilingTimelineData>(
      this.configuration._links[action].href.replace(
        "application-name-placeholder",
        encodeURIComponent(props.applicationName)
      ),
      "GET",
      {
        from: getUnixTime(start).toString(),
        to: getUnixTime(end).toString(),
        profile_type: props.profileTypeName
      },
      {},
      0,
      { signal }
    ).then(resp => {
      for (const point of resp.points) {
        point.date = new Date(point.timestamp * 1000);
      }

      return resp;
    });
  }

  getContinuousProfilingFlamebearerData(
    props: GetContinuousProfilingFlamebearerDataType,
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineContinuousProfilingFlamebearerData> {
    const action = "conprof_flamegraph";
    this.assertActionSupport(action);

    const { start, end } = requireTimeframeRange(props.timeFrame);
    return apiProxyRequest<ObservabilityPipelineContinuousProfilingFlamebearerData>(
      this.configuration._links[action].href.replace(
        "application-name-placeholder",
        encodeURIComponent(props.applicationName)
      ),
      "GET",
      {
        from: getUnixTime(start).toString(),
        to: getUnixTime(end).toString(),
        profile_type: props.profileTypeName,
        out: "flamebearerv2"
      },
      {},
      0,
      { signal }
    ).then(resp => {
      resp.flamebearer = convertContinuousProfilingProfile(
        // API response differs from the library's expected input shape
        resp as unknown as ProfilingProfileInput,
        props.language
      );

      return resp;
    });
  }

  getLogsOverview(
    timeframe: TimeFrameType,
    selection: FilterSelectValueMap,
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineLogsOverview> {
    const action = "logs_overview";
    this.assertActionSupport(action);

    const searchParams = this.buildURLSearchParams(timeframe, selection);

    return this.get<ObservabilityPipelineLogsOverview>(
      this.configuration._links[action].href + "?" + searchParams.toString(),
      signal
    );
  }

  async *queryLogs(
    timeframe: TimeFrameType,
    selection: FilterSelectValueMap,
    signal?: AbortSignal
  ): AsyncGenerator<ObservabilityPipelineLogs> {
    const action = "logs_query";
    this.assertActionSupport(action);

    const searchParams = this.buildURLSearchParams(timeframe, selection);
    let cursor = "";

    while (true) {
      if (cursor !== "") {
        searchParams.set("cursor", cursor);
      }

      const response = await this.get<ObservabilityPipelineLogs>(
        this.configuration._links[action].href + "?" + searchParams.toString(),
        signal
      );

      // No more logs matching this query unless user changes search params.
      if (false === response._has_more_results) {
        yield response;
        return;
      }
      if (cursor !== response._cursor) {
        cursor = response._cursor;
      }

      yield response;
    }
  }

  getResourcesOverview(
    timeframe: TimeFrameType,
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineResourcesOverview> {
    const action = "resources_overview";
    this.assertActionSupport(action);
    const { start, end } = requireTimeframeRange(timeframe);

    const searchParams = new URLSearchParams({
      from: getUnixTime(start).toString(),
      to: getUnixTime(end).toString()
    });
    searchParams.append("types[]", "cpu");
    searchParams.append("types[]", "memory");
    searchParams.append("types[]", "disk");

    // available aggregations are "min", "max", "avg", "stddev", "p50", "p95", "p96", "p97", "p98", "p99"
    searchParams.append("aggs[]", "avg");

    // multiple aggregations can be retrieved in the same query
    // searchParams.append("aggs[]", "p50");
    // searchParams.append("aggs[]", "p95");

    return this.get<ObservabilityPipelineResourcesOverview>(
      this.configuration._links[action].href + "?" + searchParams.toString(),
      signal
    );
  }

  getResourcesByService(
    props: GetResourceByServiceType & { aggregations?: string[] },
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineResourcesByService | undefined> {
    const action = "resources_by_service";
    const { service, timeFrame, aggregations = ["avg"] } = props;
    const { start, end } = requireTimeframeRange(timeFrame);

    this.assertActionSupport(action);

    // `resources_by_service` may be present-but-empty on misconfigured
    // clusters; resolve `undefined` so the UI shows No-data instead of
    // throwing — consumers already treat absent data as a no-op.
    const serviceHref = this.configuration._links[action]?.[service]?.href;
    if (!serviceHref) {
      return Promise.resolve(undefined);
    }

    const searchParams = new URLSearchParams({
      from: getUnixTime(start).toString(),
      to: getUnixTime(end).toString()
    });
    searchParams.append("types[]", "cpu");
    searchParams.append("types[]", "memory");
    searchParams.append("types[]", "disk");
    searchParams.append("types[]", "cpu_pressure");
    searchParams.append("types[]", "memory_pressure");
    searchParams.append("types[]", "io_pressure");

    // available aggregations are "min", "max", "avg", "stddev", "p50", "p95", "p96", "p97", "p98", "p99"

    aggregations.forEach(agg => {
      searchParams.append("aggs[]", agg);
    });

    return this.get<ObservabilityPipelineResourcesByService>(
      serviceHref + "?" + searchParams.toString(),
      signal
    );
  }

  getHttpMetricsTimeline(
    timeFrame: TimeFrameType,
    signal?: AbortSignal
  ): Promise<ObservabilityPipelineHttpMetricsTimeline> {
    const action = "http_metrics_timeline_urls";
    this.assertActionSupport(action);
    const { start, end } = requireTimeframeRange(timeFrame);

    const searchParams = new URLSearchParams({
      from: getUnixTime(start).toString(),
      to: getUnixTime(end).toString(),
      breakdown_limit: MAX_TOP_HITS.toString()
    });

    return this.get<ObservabilityPipelineHttpMetricsTimeline>(
      this.configuration._links[action]!.href + "?" + searchParams.toString(),
      signal
    );
  }

  getKey(): string {
    return `${this.projectId}/${this.branchMachineName}`;
  }

  private assertActionSupport(action: string) {
    if (action in this.configuration._links === false) {
      throw new Error(`unsupported action "${action}"`);
    }
  }

  private get<T>(url: string, signal?: AbortSignal): Promise<T> {
    return apiProxyRequest<T>(url, "GET", undefined, {}, 0, { signal });
  }

  private buildURLSearchParams(
    timeframe: TimeFrameType,
    fsvm: FilterSelectValueMap
  ): URLSearchParams {
    const { start, end } = requireTimeframeRange(timeframe);

    const searchParams = new URLSearchParams({
      from: getUnixTime(start).toString(),
      to: getUnixTime(end).toString()
    });

    Object.keys(fsvm).forEach(filterLabel => {
      const normalizedLabel = toLogFilterParam(filterLabel);
      const modeFilter = `${normalizedLabel}_mode`;
      const filter = fsvm[filterLabel];

      for (const val of filter.values) {
        searchParams.append(`${normalizedLabel}[]`, val);
      }

      searchParams.append(modeFilter, `${filter.mode}`);
    });

    return searchParams;
  }
}

export default ObservabilityPipelineHttpClient;
